import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Car, CheckCircle, MapPin, Percent, ShieldCheck, X } from "@phosphor-icons/react";
import { Button } from "./ui-kit";

const visibleRoles = new Set(["customer", "driver"]);

function firstName(name) {
  return (name || "Nexora").trim().split(/\s+/)[0] || "Nexora";
}

export default function WelcomeMessage({ user }) {
  const navigate = useNavigate();
  const storageKey = user?.id ? `nexora-welcome-seen:${user.id}` : "nexora-welcome-seen:guest";
  const [open, setOpen] = useState(false);

  const content = useMemo(() => {
    if (user?.role === "driver") {
      return {
        title: "Bienvenue dans votre espace chauffeur",
        body: `${firstName(user?.name)}, recevez les demandes proches, confirmez les paiements directs et gardez vos documents a jour pour rester visible.`,
        cta: "Voir les demandes",
        path: "/driver",
        badge: "CHAUFFEUR ACTIF",
        points: [
          { icon: MapPin, title: "Demandes proches", text: "Les courses compatibles apparaissent selon votre position et votre vehicule." },
          { icon: CheckCircle, title: "Validation claire", text: "Documents, profil et statut restent consultables par l'administration." },
          { icon: ShieldCheck, title: "Courses securisees", text: "PIN client, suivi GPS et confirmation paiement direct." },
        ],
      };
    }
    return {
      title: "Profitez de votre bienvenue Nexora",
      body: `${firstName(user?.name)}, votre compte est pret. Commandez une course, suivez le chauffeur en direct et payez directement par cash ou Mobile Money.`,
      cta: "Faire un voyage",
      path: "/app/ride",
      badge: "BIENVENUE",
      points: [
        { icon: Percent, title: "Promotions disponibles", text: "Les offres visibles dans l'app sont appliquees automatiquement quand elles sont actives." },
        { icon: MapPin, title: "Trajets clairs", text: "Choisissez le depart et l'arrivee sur la carte avec estimation avant validation." },
        { icon: ShieldCheck, title: "Plus de securite", text: "Verification email, PIN de course, profil chauffeur et historique restent disponibles." },
      ],
    };
  }, [user]);

  useEffect(() => {
    if (!user?.role || !visibleRoles.has(user.role)) return;
    const alreadySeen = localStorage.getItem(storageKey) === "1";
    if (!alreadySeen) {
      const timer = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(timer);
    }
  }, [storageKey, user?.role]);

  const close = () => {
    localStorage.setItem(storageKey, "1");
    setOpen(false);
  };

  const go = () => {
    close();
    navigate(content.path);
  };

  if (!user || !visibleRoles.has(user.role)) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[4200] flex items-end justify-center bg-black/70 p-3 backdrop-blur-md sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label="Message de bienvenue Nexora"
        >
          <motion.div
            className="relative max-h-[92svh] w-full max-w-md overflow-hidden rounded-[2rem] border border-white/10 bg-black text-white shadow-[0_30px_120px_rgba(0,0,0,0.7)]"
            initial={{ y: 28, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 24, scale: 0.98, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
          >
            <button
              onClick={close}
              className="absolute right-4 top-4 z-20 rounded-full border border-white/10 bg-white/10 p-2 text-white press-down"
              aria-label="Fermer le message de bienvenue"
            >
              <X size={18} weight="bold" />
            </button>

            <div className="max-h-[92svh] overflow-y-auto">
              <div className="relative px-6 pb-8 pt-8">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-300/30 bg-emerald-400/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 shadow-[0_0_14px_rgba(110,231,183,0.9)]" />
                  {content.badge}
                </div>
                <h2 className="font-heading text-4xl font-black leading-[1.06] text-emerald-200 sm:text-5xl">
                  {content.title}
                </h2>
                <p className="mt-4 text-sm leading-6 text-white/86">
                  {content.body}
                </p>
                <Button variant="secondary" onClick={go} className="mt-7 rounded-xl bg-white px-5 py-3 text-black hover:bg-white/90">
                  {content.cta}
                </Button>
              </div>

              <div className="relative h-44 overflow-hidden bg-[#13a34a]">
                <div className="absolute inset-0 opacity-95">
                  <div className="absolute left-8 top-5 h-[150px] w-1.5 rounded-full bg-white" />
                  <div className="absolute left-8 top-20 h-1.5 w-[78%] rounded-full bg-white" />
                  <div className="absolute left-24 top-0 h-24 w-1.5 rounded-full bg-white" />
                  <div className="absolute left-24 top-[84px] h-20 w-1.5 rounded-full bg-white" />
                  <div className="absolute right-10 top-4 h-[170px] w-1.5 rotate-45 rounded-full bg-white" />
                  <div className="absolute right-0 top-28 h-1.5 w-32 rounded-full bg-white" />
                </div>
                <motion.div
                  className="absolute bottom-9 left-[-70px] text-white drop-shadow-[0_8px_18px_rgba(0,0,0,0.35)]"
                  animate={{ x: [0, 280, 560] }}
                  transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Car size={64} weight="fill" />
                </motion.div>
              </div>

              <div className="space-y-5 px-6 py-7">
                <h3 className="font-heading text-2xl font-black leading-tight text-white">
                  Votre journee, a votre facon
                </h3>
                {content.points.map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white">
                      <item.icon size={18} weight="fill" />
                    </div>
                    <div>
                      <div className="text-sm font-black text-white">{item.title}</div>
                      <div className="mt-1 text-xs leading-5 text-white/72">{item.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
